import type { i18n } from '@/i18n'
import router from '@/router'
import { GameSession } from '@/services/game-session'
import { useEntitlementsStore } from '@/stores/entitlements'
import { useInstantWinsStore } from '@/stores/instant-wins'
import type { UserStore } from 'env'
import { TYPE } from 'vue-toastification'
import type { useToast } from 'vue-toastification'

import { RealTimeEvents } from './real-time-events'

type RewardToastSystemInitializeParams = {
  toast: ReturnType<typeof useToast>
  userStore: UserStore
  i18n: typeof i18n.global
}

// Constants
export const PENDING_REFERRAL_BOX_CODES_KEY = 'pending_referral_box_codes'
const MAX_PROCESSED_KEYS = 200

// Type definitions for reward event payloads
type RewardEventToastMetadata = {
  message_authenticated?: string
  message_unauthenticated?: string
  cta_url?: string
}

type RewardEventCredit = {
  amount: number
  reservation_id?: string
  message_id?: string
  metadata?: {
    toast?: RewardEventToastMetadata
  }
  campaign_metadata?: {
    toast?: RewardEventToastMetadata
  }
}

type RewardEventXP = {
  points: number
  reservation_id?: string
  message_id?: string
}

type RewardEventEntitlement = {
  entitlement_id?: number
  type: string
  units?: number
  target_type?: string
  target_id?: number
  plan_label?: string
  reservation_id?: string
  message_id?: string
  metadata?: {
    toast?: RewardEventToastMetadata
  }
  campaign_metadata?: {
    toast?: RewardEventToastMetadata
  }
}

type RewardEventReferralBox = {
  referral_box_code: string
  reservation_id?: string
  message_id?: string
}

type RewardEventPayload = {
  credits?: RewardEventCredit[]
  xp?: RewardEventXP[]
  entitlements?: RewardEventEntitlement[]
  referral_boxes?: RewardEventReferralBox[]
}

// Union type for items that can be deduplicated
type DedupableRewardEventItem =
  | RewardEventCredit
  | RewardEventXP
  | RewardEventEntitlement
  | RewardEventReferralBox

/**
 * Generic toast notification system for rewards.
 * Handles both simple credit amounts and complex reward payloads.
 */
export class RewardToastSystem {
  // Track active subscriptions to prevent duplicates
  private static activeSubscriptions = new Set<string>()
  private static processedEventKeys = new Set<string>()
  private static processedEventQueue: string[] = []
  private static shownEntitlementIds = new Set<number>()
  private static shownEntitlementQueue: number[] = []
  private static redeemingEntitlementIds = new Set<number>()
  private static initialized = false
  private static warnedNotInitialized = false

  private static pluralize(key: string, count: number): string {
    return RewardToastSystem.i18n.t(key, { n: count, count }, count)
  }

  private static openCta(ctaUrl?: string) {
    if (!ctaUrl) {
      return
    }
    try {
      if (ctaUrl.startsWith('/')) {
        void router.push({ path: ctaUrl })
      } else {
        window.location.href = ctaUrl
      }
    } catch (error: unknown) {
      console.error('Failed to open CTA URL', error)
    }
  }

  private static ensureInitialized(): boolean {
    if (RewardToastSystem.initialized) {
      return true
    }
    if (!RewardToastSystem.warnedNotInitialized) {
      console.warn('[RewardToastSystem] initialize() was not called before use')
      RewardToastSystem.warnedNotInitialized = true
    }
    return false
  }

  private static addProcessedKey(key: string) {
    RewardToastSystem.processedEventKeys.add(key)
    RewardToastSystem.processedEventQueue.push(key)
    if (RewardToastSystem.processedEventQueue.length > MAX_PROCESSED_KEYS) {
      const oldest = RewardToastSystem.processedEventQueue.shift()
      if (oldest) {
        RewardToastSystem.processedEventKeys.delete(oldest)
      }
    }
  }

  private static registerDedupKey(
    type: string,
    item: DedupableRewardEventItem
  ): boolean {
    const keyParts = [
      type,
      item?.reservation_id ?? 'na',
      item?.message_id ?? 'na',
    ]

    if (!item?.reservation_id && !item?.message_id) {
      keyParts.push(JSON.stringify(item))
    }

    const key = keyParts.join('|')

    if (RewardToastSystem.processedEventKeys.has(key)) {
      return false
    }

    RewardToastSystem.addProcessedKey(key)
    return true
  }

  public static routeToReferralBox(code: string) {
    try {
      const current = router.currentRoute.value
      if (current.name === 'referral-box' && current.query?.code === code) {
        // Already on the same code route; no-op
        return
      }
      if (current.name === 'referral-box') {
        void router.replace({ name: 'referral-box', query: { code } })
      } else {
        void router.push({ name: 'referral-box', query: { code } })
      }
    } catch (error: unknown) {
      console.error('Failed to route to referral box', error)
    }
  }

  private static resolveInstantWinTitle(): string | null {
    const code = router.currentRoute.value.params.instantWinCode
    if (typeof code !== 'string' || code === '') {
      return null
    }
    return useInstantWinsStore().instantWins.get(code)?.title ?? null
  }

  private static async redeemAndPlay(
    entitlementId: number,
    toastId: string | number
  ): Promise<void> {
    if (RewardToastSystem.redeemingEntitlementIds.has(entitlementId)) {
      return
    }

    RewardToastSystem.redeemingEntitlementIds.add(entitlementId)

    try {
      RewardToastSystem.toast.update(toastId, {
        content: 'Setting up your game...',
        options: {
          type: TYPE.INFO,
          timeout: false,
          closeOnClick: false,
          onClick: () => {},
        },
      })

      const entitlementsStore = useEntitlementsStore()
      const response = await entitlementsStore.redeemEntitlement(entitlementId)

      if (!response?.data?.game_session_id) {
        RewardToastSystem.toast.update(toastId, {
          content:
            entitlementsStore.error ??
            'Unable to start your game. Please try again.',
          options: {
            type: TYPE.ERROR,
            timeout: 5000,
            closeOnClick: true,
          },
        })
        return
      }

      const gameSessionId = response.data.game_session_id

      let instantWinCode = router.currentRoute.value.params
        .instantWinCode as string

      if (!instantWinCode) {
        const sessionData = await GameSession.getGameSession(gameSessionId)
        instantWinCode = sessionData.game_session.draw_code
      }

      if (!instantWinCode) {
        console.error('Unable to resolve instant win code for game session')
        RewardToastSystem.toast.update(toastId, {
          content: 'Unable to find your game. Please try again.',
          options: {
            type: TYPE.ERROR,
            timeout: 5000,
            closeOnClick: true,
          },
        })
        return
      }

      RewardToastSystem.toast.update(toastId, {
        content: 'Redirecting to your game...',
        options: {
          type: TYPE.SUCCESS,
          timeout: 1500,
          closeOnClick: true,
        },
      })

      await router.push({
        name: 'instant-wins-play',
        params: { instantWinCode },
        query: { gameSessionId },
      })
    } catch (error) {
      console.error('Failed to redeem entitlement:', error)
      RewardToastSystem.toast.update(toastId, {
        content: 'Unable to start your game. Please try again.',
        options: {
          type: TYPE.ERROR,
          timeout: 5000,
          closeOnClick: true,
        },
      })
    } finally {
      RewardToastSystem.redeemingEntitlementIds.delete(entitlementId)
    }
  }

  private static refreshPendingEntitlementsForCurrentInstantWin(): void {
    const entitlementsStore = useEntitlementsStore()
    const currentRoute = router.currentRoute.value
    const instantWinCode = currentRoute.params.instantWinCode

    if (typeof instantWinCode === 'string' && instantWinCode !== '') {
      void entitlementsStore
        .fetchPendingEntitlements('instant_win', instantWinCode)
        .catch((error: unknown) => {
          console.error('Failed to refresh pending entitlements', error)
        })

      return
    }

    void entitlementsStore
      .fetchPendingEntitlements()
      .catch((error: unknown) => {
        console.error('Failed to refresh pending entitlements', error)
      })
  }

  private static toast: RewardToastSystemInitializeParams['toast']
  private static userStore: RewardToastSystemInitializeParams['userStore']
  private static i18n: RewardToastSystemInitializeParams['i18n']

  public static initialize({
    toast,
    userStore,
    i18n,
  }: RewardToastSystemInitializeParams): void {
    RewardToastSystem.toast = toast
    RewardToastSystem.userStore = userStore
    RewardToastSystem.i18n = i18n
    RewardToastSystem.initialized = true
  }

  /**
   * Shows a credit reward notification.
   * @param {number} creditsAmount - Credit amount in pence or pounds
   * @param {boolean} isPence - Whether the amount is in pence (default: false)
   */
  static showCreditToast(
    creditsAmount: number,
    isPence: boolean = false,
    messageOverride?: string,
    ctaUrl?: string
  ): void {
    if (!RewardToastSystem.ensureInitialized()) {
      return
    }

    const amount = isPence ? creditsAmount / 100 : creditsAmount
    const formatted = RewardToastSystem.i18n.n(amount, 'currency')
    const defaultMessage = RewardToastSystem.userStore.isLoggedIn
      ? `You've received ${formatted} credit!`
      : `You've received ${formatted}! Sign in to view it.`
    const message = messageOverride ?? defaultMessage

    RewardToastSystem.toast.success(message, {
      timeout: false,
      closeOnClick: Boolean(ctaUrl),
      onClick: () => RewardToastSystem.openCta(ctaUrl),
    })
  }

  /**
   * Shows an XP reward notification.
   * @param {number} xpAmount - XP points earned
   */
  static showXPToast(xpAmount: number): void {
    if (!RewardToastSystem.ensureInitialized()) {
      return
    }

    const message = `You've earned ${xpAmount} XP points!`
    RewardToastSystem.toast.success(message, { timeout: false })
  }

  /**
   * Shows an entitlement reward notification.
   * @param {string} type - Entitlement type
   * @param {number} units - Number of units (for instant_win, etc.)
   */
  static showEntitlementToast(
    entitlement: RewardEventEntitlement,
    label?: string,
    ctaUrl?: string
  ): void {
    if (router.currentRoute.value.name === 'reward-claim') {
      return
    }

    if (!RewardToastSystem.ensureInitialized()) {
      return
    }

    if (
      entitlement.entitlement_id !== undefined &&
      RewardToastSystem.shownEntitlementIds.has(entitlement.entitlement_id)
    ) {
      return
    }

    if (entitlement.entitlement_id !== undefined) {
      RewardToastSystem.shownEntitlementIds.add(entitlement.entitlement_id)
      RewardToastSystem.shownEntitlementQueue.push(entitlement.entitlement_id)

      if (RewardToastSystem.shownEntitlementQueue.length > MAX_PROCESSED_KEYS) {
        const oldestEntitlementId =
          RewardToastSystem.shownEntitlementQueue.shift()
        if (oldestEntitlementId !== undefined) {
          RewardToastSystem.shownEntitlementIds.delete(oldestEntitlementId)
        }
      }
    }

    const type = entitlement.type
    const units = entitlement.units ?? 1
    let message = ''

    switch (type) {
      case 'instant_win': {
        const gameTitle = RewardToastSystem.resolveInstantWinTitle()
        const entriesLabel = RewardToastSystem.pluralize('label.entries', units)
        const freeGameEntriesLabel = RewardToastSystem.pluralize(
          'label.free_game_entries',
          units
        )
        message = gameTitle
          ? RewardToastSystem.i18n.t(
              'toast.entitlements.instant_win_with_title',
              {
                entries: entriesLabel,
                title: gameTitle,
              }
            )
          : RewardToastSystem.i18n.t('toast.entitlements.instant_win_generic', {
              entries: freeGameEntriesLabel,
            })
        break
      }
      case 'plan':
        message = label
          ? `You've received a ${label} plan!`
          : 'You received a discounted plan!'
        break
      case 'coupon':
        message = 'You received a discount coupon!'
        break
      default:
        message = `You received a ${type} reward!`
    }

    if (!message) {
      return
    }

    const isRedeemable =
      type === 'instant_win' && entitlement.entitlement_id !== undefined

    const toastId = RewardToastSystem.toast.success(message, {
      timeout: false,
      closeOnClick: !isRedeemable,
      onClick: isRedeemable
        ? () => {
            void RewardToastSystem.redeemAndPlay(
              entitlement.entitlement_id!,
              toastId
            )
          }
        : () => RewardToastSystem.openCta(ctaUrl),
    })
  }

  /**
   * Handles rewards claimed broadcast events.
   * @param {RewardEventPayload} event - The broadcast event payload
   */
  static async handleRewardsBroadcast(event: RewardEventPayload) {
    if (!RewardToastSystem.ensureInitialized()) {
      console.warn(
        '[RewardToastSystem] Dropped reward event due to uninitialized system:',
        event
      )
      return
    }

    await RewardToastSystem.processCreditRewards(event?.credits)
    await RewardToastSystem.processXpRewards(event?.xp)
    await RewardToastSystem.processEntitlementRewards(event?.entitlements)
    await RewardToastSystem.processReferralBoxes(event?.referral_boxes)
  }

  private static async processCreditRewards(
    credits?: RewardEventCredit[]
  ): Promise<void> {
    if (!Array.isArray(credits) || credits.length === 0) {
      return
    }

    let totalNewCredits = 0
    let anyPerItemMessage = false

    for (const credit of credits) {
      if (!RewardToastSystem.registerDedupKey('credit', credit)) {
        continue
      }

      totalNewCredits += credit.amount ?? 0
      const toastMeta =
        credit.metadata?.toast || credit.campaign_metadata?.toast
      const message = toastMeta
        ? RewardToastSystem.userStore.isLoggedIn
          ? (toastMeta.message_authenticated ?? null)
          : (toastMeta.message_unauthenticated ?? null)
        : null

      if (message) {
        anyPerItemMessage = true
        RewardToastSystem.showCreditToast(
          credit.amount,
          true,
          message,
          toastMeta?.cta_url
        )
      }
    }

    if (!anyPerItemMessage && totalNewCredits > 0) {
      RewardToastSystem.showCreditToast(totalNewCredits, true)
    }

    if (totalNewCredits > 0 && RewardToastSystem.userStore.isLoggedIn) {
      void RewardToastSystem.userStore
        .fetchCreditBalance()
        .catch((error: unknown) => {
          console.error('Failed to refresh credit balance', error)
        })
    }
  }

  private static async processXpRewards(
    rewards?: RewardEventXP[]
  ): Promise<void> {
    if (!Array.isArray(rewards) || rewards.length === 0) {
      return
    }

    let totalNewXp = 0

    for (const xp of rewards) {
      if (!RewardToastSystem.registerDedupKey('xp', xp)) {
        continue
      }

      totalNewXp += xp.points ?? 0
    }

    if (totalNewXp > 0) {
      RewardToastSystem.showXPToast(totalNewXp)
      if (
        RewardToastSystem.userStore.isLoggedIn &&
        RewardToastSystem.userStore.user?.user_progression
      ) {
        RewardToastSystem.userStore.user.user_progression.xp_earned +=
          totalNewXp
      }
    }
  }

  private static async processEntitlementRewards(
    entitlements?: RewardEventEntitlement[]
  ): Promise<void> {
    if (!Array.isArray(entitlements) || entitlements.length === 0) {
      return
    }

    for (const entitlement of entitlements) {
      if (!RewardToastSystem.registerDedupKey('entitlement', entitlement)) {
        continue
      }

      const toastMeta =
        entitlement.metadata?.toast || entitlement.campaign_metadata?.toast
      const label = toastMeta
        ? RewardToastSystem.userStore.isLoggedIn
          ? (toastMeta.message_authenticated ?? undefined)
          : (toastMeta.message_unauthenticated ?? undefined)
        : undefined

      RewardToastSystem.showEntitlementToast(
        entitlement,
        label,
        toastMeta?.cta_url
      )
    }

    const hasInstantWinEntitlement = entitlements.some(
      (entitlement) =>
        entitlement.type === 'instant_win' ||
        entitlement.target_type === 'instant_win'
    )

    if (hasInstantWinEntitlement) {
      RewardToastSystem.refreshPendingEntitlementsForCurrentInstantWin()
    }
  }

  private static async processReferralBoxes(
    referralBoxes?: RewardEventReferralBox[]
  ): Promise<void> {
    if (!Array.isArray(referralBoxes) || referralBoxes.length === 0) {
      return
    }

    for (const referralBox of referralBoxes) {
      if (!RewardToastSystem.registerDedupKey('referral_box', referralBox)) {
        continue
      }

      const code = referralBox.referral_box_code

      if (!code) {
        continue
      }

      if (RewardToastSystem.userStore.isLoggedIn) {
        RewardToastSystem.routeToReferralBox(code)
        continue
      }

      try {
        const rawExisting =
          localStorage.getItem(PENDING_REFERRAL_BOX_CODES_KEY) || '[]'
        const parsedExisting = JSON.parse(rawExisting) as unknown
        const existingCodes = Array.isArray(parsedExisting)
          ? parsedExisting.filter(
              (item): item is string => typeof item === 'string'
            )
          : []

        if (!existingCodes.includes(code)) {
          existingCodes.push(code)
          localStorage.setItem(
            PENDING_REFERRAL_BOX_CODES_KEY,
            JSON.stringify(existingCodes)
          )
        }
      } catch (error: unknown) {
        console.error('Failed to persist referral box code', error)
      }
    }
  }

  /**
   * Sets up rewards subscription for a user.
   * @param {number} userId - The user ID
   * @returns {boolean} - True if subscription was created, false if already exists
   */
  static async setupRewardsSubscription(userId: number): Promise<boolean> {
    const subscriptionKey = `customer.${userId}`

    // Check if already subscribed
    if (this.activeSubscriptions.has(subscriptionKey)) {
      console.log(`Already subscribed to rewards for customer ${userId}`)
      return false
    }

    try {
      const customerChannelName = RealTimeEvents.getChannel('Customer', userId)

      RealTimeEvents.subscribe(
        customerChannelName,
        RealTimeEvents.Events.RewardsClaimed,
        true, // public channel
        (event: RewardEventPayload) => {
          void this.handleRewardsBroadcast(event)
        }
      )

      this.activeSubscriptions.add(subscriptionKey)
      console.log(`Subscribed to rewards claimed events for customer ${userId}`)
      return true
    } catch (error: unknown) {
      console.error('Failed to setup rewards subscription:', error)
      return false
    }
  }

  /**
   * Cleans up rewards subscription for a user.
   * @param {number} userId - The user ID
   * @returns {boolean} - True if subscription was removed, false if not found
   */
  static async cleanupRewardsSubscription(userId: number): Promise<boolean> {
    const subscriptionKey = `customer.${userId}`

    if (!this.activeSubscriptions.has(subscriptionKey)) {
      return false
    }

    try {
      const customerChannelName = RealTimeEvents.getChannel('Customer', userId)

      RealTimeEvents.unsubscribe(
        customerChannelName,
        RealTimeEvents.Events.RewardsClaimed
      )
      this.activeSubscriptions.delete(subscriptionKey)

      console.log(
        `Unsubscribed from rewards claimed events for customer ${userId}`
      )
      return true
    } catch (error: unknown) {
      console.error('Failed to cleanup rewards subscription:', error)
      return false
    }
  }

  /**
   * Cleans up all active subscriptions.
   * Should be called on logout or app cleanup.
   */
  static async cleanupAllSubscriptions(): Promise<void> {
    for (const subscriptionKey of this.activeSubscriptions) {
      try {
        const userId = subscriptionKey.replace('customer.', '')
        const customerChannelName = RealTimeEvents.getChannel(
          'Customer',
          parseInt(userId)
        )

        RealTimeEvents.unsubscribe(
          customerChannelName,
          RealTimeEvents.Events.RewardsClaimed
        )
      } catch (error: unknown) {
        console.error('Error cleaning up subscription:', subscriptionKey, error)
      }
    }

    this.activeSubscriptions.clear()
    this.processedEventKeys.clear()
    this.processedEventQueue.length = 0
    this.shownEntitlementIds.clear()
    this.shownEntitlementQueue.length = 0
    this.redeemingEntitlementIds.clear()
    console.log('Cleaned up all rewards subscriptions and processed event keys')
  }
}
